import React, {useEffect, useState} from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Button,
	MenuItem,
	makeStyles, 
	InputAdornment, 
	CircularProgress,
	Card,
	CardContent,
	Typography,
	Chip,
	Box
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import LinkIcon from "@material-ui/icons/Link";
import SearchIcon from "@material-ui/icons/Search";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import ErrorIcon from "@material-ui/icons/Error";
import useKessPaymentLink from "../../../../services/useKessPaymentLink";
import useAlert from "../../../../services/useAlert";

const useStyles = makeStyles((theme) => ({
	addButton: {
		backgroundColor: '#9cc54b',
		color: '#fff',
		'&:hover': {
			backgroundColor: '#8bb140',
		},
	},
	fetchButton: {
		backgroundColor: '#9cc54b',
		color: '#fff',
		marginTop: theme.spacing(1),
		'&:hover': {
			backgroundColor: '#8bb140',
		},
	},
	toggleGroup: {
		marginBottom: theme.spacing(2),
		'& .MuiToggleButton-root': {
			textTransform: 'none',
			padding: theme.spacing(1.5, 2),
		},
		'& .Mui-selected': {
			backgroundColor: 'rgba(156, 197, 75, 0.15)',
			color: '#9cc54b',
			fontWeight: 600,
		},
	},
	verifiedSuccess: {
		color: '#4caf50',
		display: 'flex',
		alignItems: 'center',
		marginTop: theme.spacing(1),
	},
	verifiedError: {
		color: '#f44336',
		display: 'flex',
		alignItems: 'center',
		marginTop: theme.spacing(1),
	},
	dialogTitle: {
		backgroundColor: 'white',
		color: '#333',
		padding: theme.spacing(2, 3),
		display: 'flex',
		alignItems: 'center',
		borderBottom: '2px solid #9cc54b',
		'& svg': {
			marginRight: theme.spacing(1),
			color: '#9cc54b',
		},
	},
	sectionDivider: {
		margin: theme.spacing(2, 0),
		borderBottom: `1px solid ${theme.palette.divider}`,
	},
	fetchCard: {
		backgroundColor: '#fafafa',
		borderRadius: theme.spacing(1),
		padding: theme.spacing(2),
		marginTop: theme.spacing(2),
		border: '1px solid rgba(156, 197, 75, 0.3)',
	},
	successCard: {
		backgroundColor: 'rgba(156, 197, 75, 0.1)',
		borderColor: '#9cc54b',
	},
	errorCard: {
		backgroundColor: '#ffebee',
		borderColor: '#f44336',
	},
	fieldLabel: {
		fontWeight: 500,
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(0.5),
	},
	// Modern input styling
	modernInput: {
		'& .MuiOutlinedInput-root': {
			borderRadius: 12,
			backgroundColor: '#fff',
			transition: 'all 0.2s ease',
			'&:hover': {
				backgroundColor: '#fafafa',
			},
			'&.Mui-focused': {
				boxShadow: '0 0 0 3px rgba(156, 197, 75, 0.2)',
			},
		},
		'& .MuiInputLabel-root': {
			fontWeight: 500,
		},
		'& .MuiInputLabel-root.Mui-focused': {
			color: '#9cc54b',
		},
	},
	formSection: {
		backgroundColor: '#fff',
		borderRadius: 16,
		padding: theme.spacing(2),
		marginBottom: theme.spacing(2),
		border: '1px solid #e0e0e0',
	},
	sectionTitle: {
		fontSize: 14,
		fontWeight: 600,
		color: '#666',
		marginBottom: theme.spacing(1.5),
		textTransform: 'uppercase',
		letterSpacing: 0.5,
	},
}));

const AddPaymentMethodModal = ({
	                               open,
	                               onClose,
	                               handleAddPaymentMethod,
	                               paymentMethods,
	                               handleChangeMethod,
	                               formPaymentMethod,
	                               paymentModalType,
	                               storePaymentMethod,
	                               paymentType,
	                               dialogLoading,
	                               paymentReason,
	                               paymentTypeReason,
	                               refundReason,
	                               setFormPaymentMethod,
	                               orderId  // Need to pass orderId from parent
                               }) => {
	const classes = useStyles();
	const alert = useAlert();
	const {
		loading: kessLoading,
		error: kessError,
		kessPaymentData,
		paymentMethods: kessPaymentMethods,
		queryKessPayment,
		linkExistingPayment,
		linkManualPayment,
		fetchPaymentMethods,
		clearPaymentData,
		setKessPaymentData
	} = useKessPaymentLink();

	const [paymentLinkMode, setPaymentLinkMode] = useState('new'); // 'new', 'existing', or 'manual'
	const [outTradeNo, setOutTradeNo] = useState('');
	const [editablePaymentRef, setEditablePaymentRef] = useState('');
	const [editableOutTradeNo, setEditableOutTradeNo] = useState('');

	// Manual entry fields
	const [manualOutTradeNo, setManualOutTradeNo] = useState('');
	const [manualAmount, setManualAmount] = useState('');
	const [manualPaymentRef, setManualPaymentRef] = useState('');
	const [manualPaymentOption, setManualPaymentOption] = useState('');
	const [manualToken, setManualToken] = useState('');

	// Check if payment method is "Payment Link"
	const isPaymentLinkMethod = formPaymentMethod.payment_method === 'Payment Link';
	console.log(kessError)
	const handleSubmit = async () => {
		if (paymentLinkMode === 'existing' && isPaymentLinkMethod && kessPaymentData) {
			// Link existing KESS payment (with KESS API verification)
			const result = await linkExistingPayment(
				orderId,
				editableOutTradeNo || kessPaymentData.out_trade_no,
				editablePaymentRef || kessPaymentData.payment_reference,
				kessPaymentData.total_amount || kessPaymentData.amount
			);

			if (result.success) {
				alert.toast('Payment linked successfully!', 'success');
				handleModalClose();
				// Refresh the order details
				window.location.reload();
			} else {
				alert.toast(result.message || 'Failed to link payment', 'error');
			}
		} else if (paymentLinkMode === 'manual' && isPaymentLinkMethod) {
			// Manually link payment (without KESS API verification)
			// Validation
			if (!manualOutTradeNo.trim()) {
				alert.toast('Transaction reference is required', 'warning');
				return;
			}
			if (!manualAmount || parseFloat(manualAmount) <= 0) {
				alert.toast('Valid amount is required', 'warning');
				return;
			}

			const result = await linkManualPayment(
				orderId,
				manualOutTradeNo,
				{
					amount: parseFloat(manualAmount),
					payment_reference: manualPaymentRef || null,
					payment_option: manualPaymentOption || 'MANUAL',
					p_token: manualToken || null,
				}
			);

			if (result.success) {
				alert.toast('Payment manually linked successfully!', 'success');
				handleModalClose();
				// Refresh the order details
				window.location.reload();
			} else {
				alert.toast(result.message || 'Failed to manually link payment', 'error');
			}
		} else {
			// Normal payment method flow
			handleAddPaymentMethod();
		}
	};

	const handleFetchPayment = async () => {
		if (!outTradeNo.trim()) {
			alert.toast('Please enter a transaction reference number', 'warning');
			return;
		}

		const result = await queryKessPayment(outTradeNo);
		
		if (result.success) {
			alert.toast(result.message || 'Payment details fetched successfully', 'success');
			// Set editable fields with fetched data
			setEditablePaymentRef(result.data.payment_reference || '');
			setEditableOutTradeNo(result.data.out_trade_no || '');
		} else {
			alert.toast(result.message || 'Failed to fetch payment', 'error');
		}
	};

	const handleModalClose = () => {
		// Close the modal - reset handled by useEffect when open becomes false
		onClose("add");
	};

	useEffect(() => {
		if (formPaymentMethod.type === "payment") {
			const validateTransaction = paymentTypeReason.includes(formPaymentMethod.reason)
			if (!validateTransaction) {
				const target = {
					name: "reason",
					value: ""
				}
				handleChangeMethod({target})
			}
		}
	}, [formPaymentMethod.type])

	// Fetch payment methods when modal opens
	useEffect(() => {
		if (open) {
			// Fetch payment methods every time modal opens
			fetchPaymentMethods();
		}
	}, [open, fetchPaymentMethods]);

	// Reset fields when modal closes
	useEffect(() => {
		if (!open) {
			setPaymentLinkMode('new');
			setOutTradeNo('');
			setEditablePaymentRef('');
			setEditableOutTradeNo('');
			setManualOutTradeNo('');
			setManualAmount('');
			setManualPaymentRef('');
			setManualPaymentOption('');
			setManualToken('');
		}
	}, [open]);

	return (
		<Dialog open={open} onClose={handleModalClose} fullWidth maxWidth="md">
			<DialogTitle className={classes.dialogTitle} disableTypography>
				<Typography variant="h6" style={{display: 'flex', alignItems: 'center', color: '#333'}}>
					{paymentModalType === "add" ? <AddIcon style={{marginRight: 8}} /> : <EditIcon style={{marginRight: 8}} />}
					{paymentModalType === "add" ? "Add Payment" : "Edit Payment"}
				</Typography>
			</DialogTitle>
			<DialogContent style={{paddingTop: 24, backgroundColor: '#f8f9fa'}}>
				{/* Basic Info Section */}
				<Box className={classes.formSection}>
					<Typography className={classes.sectionTitle}>Payment Information</Typography>
					<TextField
						label="Date"
						type="date"
						name="date"
						fullWidth
						margin="normal"
						variant="outlined"
						className={classes.modernInput}
						InputLabelProps={{shrink: true}}
						value={formPaymentMethod.date}
						onChange={handleChangeMethod}
					/>
					<TextField
						label="Payment Method"
						name="payment_method"
						select
						fullWidth
						margin="normal"
						variant="outlined"
						className={classes.modernInput}
						value={formPaymentMethod.payment_method}
						onChange={handleChangeMethod}
					>
						{paymentMethods.map((option) => (
							<MenuItem key={option.type} value={option.title}>
								{option.title}
							</MenuItem>
						))}
					</TextField>
				</Box>

				{/* Show payment link mode toggle only for Payment Link method */}
				{isPaymentLinkMethod && (
					<Box mt={2} mb={2}>
						<Typography variant="subtitle2" gutterBottom>
							Payment Type
						</Typography>
						<ToggleButtonGroup
							className={classes.toggleGroup}
							value={paymentLinkMode}
							exclusive
							onChange={(e, newMode) => {
								if (newMode !== null) {
									setPaymentLinkMode(newMode);
									clearPaymentData();
									setOutTradeNo('');
								}
							}}
							fullWidth
						>
							<ToggleButton value="new">
								<AddIcon style={{marginRight: 8}} />
								Create New Payment
							</ToggleButton>
							<ToggleButton value="existing">
								<LinkIcon style={{marginRight: 8}} />
								Link with KESS API
							</ToggleButton>
							<ToggleButton value="manual">
								<EditIcon style={{marginRight: 8}}/>
								Manual Entry
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>
				)}

				{/* Show existing payment fields if mode is 'existing' */}
				{isPaymentLinkMethod && paymentLinkMode === 'existing' && (
					<Box>
						{/* Display error if any */}
						{kessError && (
							<Alert severity="error" style={{marginTop: 16, marginBottom: 16}}>
								{kessError}
							</Alert>
						)}
						<TextField
							label="KESS Order Ref (out_trade_no)"
							name="out_trade_no"
							fullWidth
							margin="normal"
							value={outTradeNo}
							onChange={(e) => setOutTradeNo(e.target.value)}
							placeholder="Enter KESS transaction reference"
							required
							helperText="Enter the out_trade_no from KESS payment system"
						/>
						<Button
							className={classes.fetchButton}
							onClick={handleFetchPayment}
							disabled={kessLoading || !outTradeNo.trim()}
							fullWidth
							variant="contained"
							startIcon={kessLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
						>
							{kessLoading ? 'Fetching...' : 'Fetch Payment Details'}
						</Button>

						{/* Display fetched payment data */}
						{/* Display fetched payment data as standard fields */}
						{kessPaymentData && (
							<Box className={classes.fetchCard}>
								<Box display="flex" alignItems="center" mb={2}>
									{kessPaymentData.status === 'SUCCESS' ? (
										<Chip
											icon={<CheckCircleIcon />}
											label="Payment Verified: SUCCESS"
											style={{backgroundColor: '#9cc54b', color: 'white'}}
										/>
									) : (
										<Chip
											icon={<ErrorIcon />}
											label={`Status: ${kessPaymentData.status}`}
											style={{backgroundColor: '#f44336', color: 'white'}}
										/>
									)}
								</Box>

								<TextField
									label="Amount"
									fullWidth
									margin="normal"
									value={kessPaymentData.total_amount || kessPaymentData.amount || '0.00'}
									InputProps={{
										readOnly: true,
										startAdornment: <InputAdornment position="start">$</InputAdornment>,
									}}
								/>

								<TextField
									label="Payment Method"
									fullWidth
									margin="normal"
									value={kessPaymentData.payment_detail?.method_desc || kessPaymentData.payment_detail?.payment_method?.title || kessPaymentData.payment_method || 'N/A'}
									InputProps={{
										readOnly: true,
									}}
								/>

								<TextField
									label="Order Reference (p_txnld)"
									fullWidth
									margin="normal"
									value={editableOutTradeNo}
									onChange={(e) => setEditableOutTradeNo(e.target.value)}
									helperText="You can edit this before saving"
								/>

								<TextField
									label="Bank Ref (payment_reference)"
									fullWidth
									margin="normal"
									value={editablePaymentRef}
									onChange={(e) => setEditablePaymentRef(e.target.value)}
									helperText="You can edit this before saving"
								/>
							</Box>
						)}
					</Box>
				)}

				{/* Manual Entry Mode - Admin enters all payment details manually */}
				{isPaymentLinkMethod && paymentLinkMode === 'manual' && (
					<Box className={classes.formSection}>
						{/* Display error if any */}
						{kessError && (
							<Alert severity="error" style={{marginTop: 16, marginBottom: 16}}>
								{kessError}
							</Alert>
						)}
						<Typography className={classes.sectionTitle}>Manual Payment Entry</Typography>
						<Alert severity="info" style={{marginBottom: 16}}>
							Manually enter payment information without verifying with KESS API. Only checks database for duplicates.
						</Alert>

						{/* Loading indicator for payment methods */}
						{kessLoading && (
							<Box display="flex" alignItems="center" mb={2}>
								<CircularProgress size={16} style={{marginRight: 8}}/>
								<Typography variant="body2" color="textSecondary">
									Loading payment methods from KESS...
								</Typography>
							</Box>
						)}

						<TextField
							label="Order Ref (out_trade_no) *"
							name="manual_out_trade_no"
							fullWidth
							margin="normal"
							variant="outlined"
							className={classes.modernInput}
							value={manualOutTradeNo}
							onChange={(e) => setManualOutTradeNo(e.target.value)}
							placeholder="Enter transaction reference number"
							required
							helperText="Unique transaction identifier (will be auto-uppercased)"
						/>

						<TextField
							label="Amount *"
							type="number"
							name="manual_amount"
							fullWidth
							margin="normal"
							variant="outlined"
							className={classes.modernInput}
							placeholder="0.00"
							InputProps={{
								inputProps: {min: 0, step: 0.01},
								startAdornment: <InputAdornment position="start">$</InputAdornment>,
							}}
							value={manualAmount}
							onChange={(e) => setManualAmount(e.target.value)}
							required
							helperText="Payment amount"
						/>

						<TextField
							label="Bank Ref"
							name="manual_payment_ref"
							fullWidth
							margin="normal"
							variant="outlined"
							className={classes.modernInput}
							value={manualPaymentRef}
							onChange={(e) => setManualPaymentRef(e.target.value)}
							placeholder="Bank reference or transaction ID"
							helperText="Optional: Bank reference number"
						/>

						<TextField
							label="Payment Method"
							name="manual_payment_option"
							select
							fullWidth
							margin="normal"
							variant="outlined"
							className={classes.modernInput}
							value={manualPaymentOption}
							onChange={(e) => setManualPaymentOption(e.target.value)}
							helperText={kessLoading ? "Loading payment methods from KESS..." : "Select payment method used"}
							disabled={kessLoading}
						>
							{/* Display payment methods from KESS API */}
							{kessPaymentMethods && kessPaymentMethods.length > 0 ? (
								kessPaymentMethods.map((method) => (
									<MenuItem key={method.bic || method.swift_code} value={method.swift_code || method.bic}>
										{method.title}
									</MenuItem>
								))
							) : !kessLoading ? (
								<MenuItem value="MANUAL">Manual Entry (Default)</MenuItem>
							) : null}
							{/* Always add "Other" option at the end */}
							<MenuItem value="OTHER">Other</MenuItem>
						</TextField>

						<TextField
							label="Token (Optional)"
							name="manual_token"
							fullWidth
							margin="normal"
							variant="outlined"
							className={classes.modernInput}
							value={manualToken}
							onChange={(e) => setManualToken(e.target.value)}
							placeholder="Payment token if available"
							helperText="Optional: Payment gateway token"
						/>
					</Box>
				)}

				{/* Show normal payment fields if not linking existing */}
				{(!isPaymentLinkMethod || paymentLinkMode === 'new') && (
					<Box className={classes.formSection}>
						<Typography className={classes.sectionTitle}>Transaction Details</Typography>
						{
							storePaymentMethod(formPaymentMethod.payment_method) &&
							<TextField
								label="Payment Type"
								name="type"
								select
								fullWidth
								margin="normal"
								variant="outlined"
								className={classes.modernInput}
								value={formPaymentMethod.type}
								onChange={handleChangeMethod}
							>
								{paymentType.map((option) => (
									<MenuItem key={option} value={option}>
										{option}
									</MenuItem>
								))}
							</TextField>
						}
						<TextField
							label="Amount"
							type="number"
							name="amount"
							fullWidth
							margin="normal"
							variant="outlined"
							className={classes.modernInput}
							placeholder="0.00"
							InputProps={{
								inputProps: {min: 0, step: 0.01},
								startAdornment: <InputAdornment position="start">$</InputAdornment>,
							}}
							value={formPaymentMethod.amount}
							onChange={handleChangeMethod}
						/>
						<TextField
							label="Reason"
							name="reason"
							select
							fullWidth
							margin="normal"
							variant="outlined"
							className={classes.modernInput}
							value={formPaymentMethod.reason}
							onChange={handleChangeMethod}
						>
							{(formPaymentMethod.type === "payment" ? paymentReason : refundReason).map((option) => (
								<MenuItem key={option} value={option}>
									{option}
								</MenuItem>
							))}
						</TextField>
						<TextField
							label="Comment"
							name="comment"
							fullWidth
							margin="normal"
							variant="outlined"
							className={classes.modernInput}
							multiline
							rows={3}
							placeholder="Add any additional notes..."
							value={formPaymentMethod.comment}
							onChange={handleChangeMethod}
						/>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleModalClose}>Cancel</Button>
				<Button
					onClick={handleSubmit}
					className={classes.addButton}
					disabled={
						dialogLoading ||
						kessLoading ||
						(paymentLinkMode === 'existing' && isPaymentLinkMethod && !kessPaymentData) ||
						(paymentLinkMode === 'manual' && isPaymentLinkMethod && (!manualOutTradeNo.trim() || !manualAmount || parseFloat(manualAmount) <= 0))
					}
					variant="contained"
					color="primary"
					startIcon={
						(dialogLoading || kessLoading) ? (
							<CircularProgress size={20} color="inherit"/>
						) : (
							paymentModalType === "add" ? (
								paymentLinkMode === 'existing' ? <LinkIcon/> :
									paymentLinkMode === 'manual' ? <EditIcon/> :
										<AddIcon/>
							) : <EditIcon/>
						)
					}
					style={{
						minWidth: 120,
						transition: "all 0.3s ease",
					}}
				>
					{(dialogLoading || kessLoading) ? (
						"Processing..."
					) : (
						paymentModalType === "add" ? (
							paymentLinkMode === 'existing' ? "Link Payment" :
								paymentLinkMode === 'manual' ? "Manual Link" :
									"Add Payment"
						) : "Update Payment"
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AddPaymentMethodModal;
